import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, first, from, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { ExternalAPI } from '../../../model/external-api';
import { AttachmentUtils } from './attachment.utils';
import { ViewData } from '../../../model/view.data';
import { Model } from '../../../model/model';
import { AkmBDD, BddMedia } from '@wizeoo/akm-worker-lib';
import { AllkeyWebWorker } from '../../../manager/worker/allkey.web.worker';
import { WizCommandManager, WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { Attachment, AttachmentResult } from '@wizeoo/akm-message-lib';
import { AttachmentCmd } from './attachment.cmd';


/**
 * mémorisation des données média, pour affichage dans les formulaire / les grids
 */
export interface MediaReference {
  fileName: string;
  fileType: string;
  blobId: string;
  shortFileName: string;
  v2: boolean;
  directory: string;
  attachment?: Attachment;
  kind?: string;
}

export interface BatchResultObject {
  icon: any;
  message: string;
};

@Injectable()
export class AttachmentService {
  private static _INSTANCE: AttachmentService;
  public static getInstance(): AttachmentService {
    return this._INSTANCE;
  }

  constructor(private _http: HttpClient) {
    AttachmentService._INSTANCE = this;
    // chargement des thumbs
    AttachmentService.getInstance().preloadThumbsFromIndexedDB().subscribe();
  }

  public model: Model = null;

  // Cache: [fullBytes|null, thumb|null]
  private _attachments: Map<string, [string | null, string | null]> = new Map();
  private _errors: Set<string> = new Set();
  //private _remoteCallMap: Map<string, Observable<AttachmentResult>> = new Map();
  //private _localCallMap: Map<string, Observable<any>> = new Map();

  public clearAll(): void {
    this._attachments.clear();
    this._errors.clear();
  }

  /** Génération de clé unique */
  public static attachmentUniqueKey(att: Attachment): string {
    if (att.v2) {
      return [att.rootDirectory, att.directory, att.blobId].join(ViewData.WIDGET_KEY_SEPARATOR);
    } else {
      return [att.domain, att.businessUnitCode, att.vision, att.blobId].join(ViewData.KEY_SEPARATOR);
    }
  }

  /** Construction d’un attachment sur upload */
  public static buildAttachment(file: File, content: string, domain: string, bu: string, vision: string): Attachment {
    const result: Attachment = new Attachment();
    if (file) {
      result.fileName = file.name;
      result.fileType = file.type;
    }
    if (content) result.fileContent = content;
    result.domain = domain;
    result.businessUnitCode = bu;
    result.vision = vision;
    result.v2 = true;
    return result;
  }

  /** Retourne le binaire, le code Error ou null */
  getAttachmentOrError(blobId: string, thumbMode: boolean): string {
    if (!blobId) return ViewData.ATTACHMENT_ERROR;
    if (this._attachments.has(blobId)) {
      return thumbMode ? this._attachments.get(blobId)[1] ?? ViewData.ATTACHMENT_ERROR
                       : this._attachments.get(blobId)[0];
    }

    const media: Attachment = new Attachment();
    media.domain = this.model.domainCode;
    media.businessUnitCode = this.model.businessUnitEngine.currentBU.businessUnitCode;
    media.vision = ViewData.wildcardMainVision;
    media.blobId = blobId;

    if (this._errors.has(AttachmentService.attachmentUniqueKey(media))) {
      return ViewData.ATTACHMENT_ERROR;
    }
    return null;
  }

/** Upload d’un attachment */
uploadAttachment(pAttachment: Attachment): Observable<Attachment> {
  if (!AttachmentUtils.isValid(pAttachment)) return of(pAttachment);

  return this._http.post<AttachmentResult>(ExternalAPI.ATTACHMENTS, pAttachment).pipe(
    tap((item: AttachmentResult) => {
      if (!item?.entity?.blobId) return of(item.entity);
        let realBlobId: string = item.entity.blobId;
        if(!!pAttachment.metadatas?.noEncode) {
          realBlobId = item.entity.fileName;
        }

        // console.log('item', item);
        // on mémorise seulement si c'est une image
        if(item.entity.fileType?.startsWith('image/')) {
          this._attachments.set(realBlobId, [item.entity.fileContent, item.thumb?.fileContent]);
          AllkeyWebWorker.storeMedia(realBlobId, { key: realBlobId, value: item.entity.fileContent, thumb: item.thumb?.fileContent }, AkmBDD.BDD_MEDIA_KEY);    
        }
    }),
    map((item: AttachmentResult) => item.entity)
  );
}

  /** Précharge tous les thumbs depuis IndexedDB */
  preloadThumbsFromIndexedDB(): Observable<void> {
    return from(AkmBDD.getInstance().getAll(AkmBDD.BDD_MEDIA_KEY)).pipe(
      tap((allMedia: BddMedia[]) => {
        allMedia.forEach((m) => {
          if (m?.thumb) {
            this._attachments.set(m.key, [null, m.thumb]);
          }
        });
      }),
      switchMap(() => of(void 0))
    );
  }

  /** Téléchargement d’un attachment (thumbMode=true => priorité aux thumbs locaux) */
  downloadAttachment(media: Attachment): Observable<any> {
    if (!media?.blobId) return of(null);
    const uuid = AttachmentService.attachmentUniqueKey(media);

    // déjà en erreur, end game
    if (this._errors.has(uuid)) {
      // console.log('in error', uuid);
      return of(null);
    } 


    if (this._attachments.has(media.blobId)) {
        const [full, thumb] = this._attachments.get(media.blobId);
        if (media.thumb && thumb) {
            return of(thumb); // On a bien le thumb : on le donne.
        }
        if (!media.thumb && full) {
            return of(full);  // On a bien le full : on le donne.
        }
        // Sinon, NE PAS retourner of(null), on veut poursuivre avec le fallback HTTP plus bas.
    }

    // Si une requête remote est déjà en cours pour ce blobId
    // if (this._remoteCallMap.has(uuid)) {
    //   // console.log('in remote', uuid);
    //   return this._remoteCallMap.get(uuid);
    // }


    // console.log('check local', uuid);
    // 2. Cache local (AkmBDD, IndexedDB)
    // On tente de le charger depuis AkmBDD. BDD_MEDIA_KEY contient (value: bin, thumb: thumb).
    return from(AkmBDD.getInstance().doGet(AkmBDD.BDD_MEDIA_KEY, media.blobId)).pipe(
        take(1),
        switchMap((bddMedia) => {
            if (bddMedia) {
                // On l'a trouvé en local : on le met aussi en RAM pour les prochains accès
                this._attachments.set(media.blobId, [bddMedia.value, bddMedia.thumb]);
                // Retourne-le si trouvé
                if (media.thumb && bddMedia.thumb) return of(bddMedia.thumb);
                if (!media.thumb && bddMedia.value) return of(bddMedia.value);
            }

            // console.log('check remote', uuid);
            // 3. Fallback HTTP
            // ... (le code HTTP ici, => ton code actuel)
            // la suite déjà présente, ne rien changer à partir d'ici :
            let url = `${ExternalAPI.ATTACHMENTS}?domain=${media.domain}&bu=${media.businessUnitCode}&vision=${media.vision}&blobId=${media.blobId}&thumb=${media.thumb}`;
            if(!!media.directory) {
              url+= "&directory="+media.directory;
            }
            const remoteCall$ = this._http.get<AttachmentResult>(url).pipe(
                take(1),
                switchMap((item: AttachmentResult) => {
              
              //tap((item: AttachmentResult) => { // retour de remote, on a à minima le thumb, et possiblement la vraie image
                //this._remoteCallMap.delete(uuid);
                
                if (!item || (!item.entity && (!item.thumb || item.thumb.fileContent?.length <= 0))) { // pas de thumb
                  this._errors.add(uuid);
                  return of(null);
                }

                let thumbByte: any = null;
                if(item.thumb) { // on a la source
                  thumbByte = item.thumb.fileContent;
                }

                let sourceByte: any = null;
                if(item.entity) { // on a la source
                  sourceByte = item.entity.fileContent;
                }
 
                this._attachments.set(media.blobId, [sourceByte, thumbByte]);
                AllkeyWebWorker.storeMedia(media.blobId, { key: media.blobId, value: sourceByte, thumb: thumbByte }, AkmBDD.BDD_MEDIA_KEY);
                
                

                if(media.thumb) {
                  return of(thumbByte);
                } else {
                  return of(sourceByte);
                }
      
              })
              // ,
              // finalize(() => {
              //   // Une fois la requête terminée (succès ou erreur), on supprime de la map
              //   this._remoteCallMap.delete(uuid);
              // }),
              // shareReplay(1)
            );
            // this._remoteCallMap.set(uuid, remoteCall$);
            return remoteCall$;
        })
    );
}

  /** Téléchargement pour widget */
  downloadAttachmentForWidget(blobId: string, thumbMode: boolean = true, directory: string = null): Observable<string> {
    const media: Attachment = new Attachment();
    media.domain = this.model.domainCode;
    media.businessUnitCode = this.model.businessUnitCode;
    media.vision = ViewData.wildcardMainVision;
    media.blobId = blobId;
    media.fileName = blobId;
    media.thumb = thumbMode;
    media.directory = directory;
    return this.downloadAttachment(media);
  }


  /** Téléchargement pour lien widget (file download) */
  filedownloadForWidget(mediaReference: MediaReference): void {
    // if (mediaReference?.v2) {
    //   this.downloadAttachmentV2(mediaReference);
    // } else 
      if (mediaReference?.blobId) { // vieux uploads
      
      // tester sur mediaReference => file Name => sinon blobID
      
      WizCommandManager.setLock(true);
      this.downloadAttachmentForWidget(mediaReference.blobId, false, mediaReference.directory).pipe(first()).subscribe(item => {
        if (item) {
            let base64 = item.trim();
            if (base64.startsWith('"') && base64.endsWith('"')) {
              base64 = base64.slice(1, -1);
            }
            // Décode la base64
            let decoded = atob(base64);
            // Test si decoded ressemble à une chaîne base64 (caractères, longueur multiple de 4)
            if (/^[A-Za-z0-9+/=\r\n]+$/.test(decoded) && decoded.length % 4 === 0) {
              // Probablement base64 encore une fois...
              try {
                const tryDoubleDecode = atob(decoded);
                // Si ça "sent" le CSV (par ex commence par 'client;' ou contient ';' ou ',')
                if (/client;|[,;]/.test(tryDoubleDecode)) {
                  decoded = tryDoubleDecode;
                }
              } catch (e) {
                // Not really base64, use as is
              }
            }

            // Détecte si c'est un type textuel ou non
            const mime = mediaReference.fileType?.toLowerCase() || '';
            let blob;
            if (/^text\/|\/csv$|\/xml$|\/json$/.test(mime)) {
              // Fichier texte, pas besoin de bytearray
              blob = new Blob([decoded], { type: mediaReference.fileType });
            } else {
              // Fichier binaire, construit un ArrayBuffer
              const byteArray = new Uint8Array(Array.from(decoded, ch => ch.charCodeAt(0)));
              blob = new Blob([byteArray], { type: mediaReference.fileType });
            }

            const fileUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = mediaReference.fileName;
            link.href = fileUrl;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              URL.revokeObjectURL(fileUrl);
              document.body.removeChild(link);
            }, 100);
        } else {
          WizPopUp.toast('$.nosuchfile.error.$', WizIcon.TOAST_ERROR).show();
        }
        WizCommandManager.setLock(false);
      });
    }
  }

  /** Attachment V2 classique */
  downloadAttachmentV2(media: MediaReference): void {
    if (!media?.fileName) return;
    const att: Attachment = media as unknown as Attachment;
    const uuid = AttachmentService.attachmentUniqueKey(att);
    if (this._errors.has(uuid)) return;
    new AttachmentCmd(this.model).get(att).execute();
  }

  /** Shortcut pour iconOrDie */
  iconOrDie(blobId: string, dom: string, bu: string, vision?: string, thumb: boolean = true): Observable<string> {
    const media: Attachment = new Attachment();
    media.domain = dom;
    media.businessUnitCode = bu;
    media.blobId = blobId;
    media.fileName = blobId;
    media.vision = vision ?? ViewData.wildcardMainVision;
    media.thumb = thumb;
    return this.downloadAttachment(media);
  }

  /** Récupération d’un attachment déjà en cache */
  getAttachment(blobId: string): [string, string] {
    return this._attachments.get(blobId);
  }

}
